// store/slices/cartSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import {
  getUserCartApi,
  addToCartApi,
  updateCartItemApi,
  deleteCartItemApi
} from "../../api/cartApi"

////////////////////////////////////////////////////////
// HELPER (IMPORTANT)
////////////////////////////////////////////////////////

const transformCart = (items) => {

  const cartItems = {}
  const cartIds = []

  items.forEach(item => {

    const product = item.cart_products?.[0]
    const variant = product?.product_variants?.find(
      v => v.id === item.variant_id
    )

    if (!product || !variant) return

    const image =
      variant.variant_images?.find(i => i.is_default)?.product_image ||
      variant.variant_images?.[0]?.product_image

    const cartItemId = item.id

    cartItems[cartItemId] = {
      cartItemId,
      variantId: item.variant_id,
      quantity: item.quantity,

      // ✅ SNAPSHOT DATA
      productName: product.name,
      variantName: `${variant?.colour?.[0]?.name} / ${variant?.size?.[0]?.name}`,
      image,
      price: variant.final_mrp,
      stock: variant.stock
    }

    cartIds.push(cartItemId)

  })

  return { cartItems, cartIds }

}

////////////////////////////////////////////////////////
// FETCH CART
////////////////////////////////////////////////////////

export const fetchUserCart = createAsyncThunk(
  "cart/fetchUserCart",
  async (_, { rejectWithValue }) => {
    try {

      const res = await getUserCartApi()

      const { cartItems, cartIds } =
        transformCart(res.data.data.items)

      return {
        items: cartItems,
        itemIds: cartIds,
        totals: res.data.data.totals
      }

    } catch (err) {
      return rejectWithValue(err.response?.data)
    }
  }
)

////////////////////////////////////////////////////////
// ADD TO CART
////////////////////////////////////////////////////////

export const addToCartAsync = createAsyncThunk(
  "cart/addToCart",
  async (data, { rejectWithValue }) => {
    try {

      const res = await addToCartApi(data)

      const { cartItems, cartIds } =
        transformCart(res.data.data.items)

      return {
        items: cartItems,
        itemIds: cartIds,
        totals: res.data.data.totals
      }

    } catch (err) {
      return rejectWithValue(err.response?.data)
    }
  }
)

////////////////////////////////////////////////////////
// UPDATE
////////////////////////////////////////////////////////

export const updateCartItemAsync = createAsyncThunk(
  "cart/updateCartItem",
  async (data, { rejectWithValue }) => {
    try {

      const res = await updateCartItemApi(data)

      const { cartItems, cartIds } =
        transformCart(res.data.data.items)

      return {
        items: cartItems,
        itemIds: cartIds,
        totals: res.data.data.totals
      }

    } catch (err) {
      return rejectWithValue(err.response?.data)
    }
  }
)

////////////////////////////////////////////////////////
// DELETE
////////////////////////////////////////////////////////

export const deleteCartItemAsync = createAsyncThunk(
  "cart/deleteCartItem",
  async (data, { rejectWithValue }) => {
    try {

      const res = await deleteCartItemApi(data)

      const { cartItems, cartIds } =
        transformCart(res.data.data.items)

      return {
        items: cartItems,
        itemIds: cartIds,
        totals: res.data.data.totals
      }

    } catch (err) {
      return rejectWithValue(err.response?.data)
    }
  }
)

////////////////////////////////////////////////////////
// SLICE
////////////////////////////////////////////////////////

const cartSlice = createSlice({

  name: "cart",

  initialState: {
    items: {},
    itemIds: [],
    totals: null,
    loading: false
  },

  reducers: {},

  extraReducers: builder => {

    builder

      .addCase(fetchUserCart.pending, state => {
        state.loading = true
      })

      .addCase(fetchUserCart.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items
        state.itemIds = action.payload.itemIds
        state.totals = action.payload.totals
      })

      .addCase(addToCartAsync.fulfilled, (state, action) => {
        state.items = action.payload.items
        state.itemIds = action.payload.itemIds
        state.totals = action.payload.totals
      })

      .addCase(updateCartItemAsync.fulfilled, (state, action) => {
        state.items = action.payload.items
        state.itemIds = action.payload.itemIds
        state.totals = action.payload.totals
      })

      .addCase(deleteCartItemAsync.fulfilled, (state, action) => {
        state.items = action.payload.items
        state.itemIds = action.payload.itemIds
        state.totals = action.payload.totals
      })

  }

})

export default cartSlice.reducer